import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TeacherJourneySection from './components/TeacherJourneySection';
import { Link } from 'react-router-dom';

function useVisible(threshold = 0.12) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const values = [
  { icon: 'ri-forbid-2-line', title: '不速成', desc: '沒有「30天學會古箏」這種說法。每個人的節奏不同，學習本來就該有它自己的時間。' },
  { icon: 'ri-scales-3-line', title: '不比較', desc: '這裡沒有進度排名，沒有同學之間的比較。你唯一的參照點，是你自己上一堂課的狀態。' },
  { icon: 'ri-hand-heart-line', title: '不勉強', desc: '如果某個階段你需要暫停，或者想放慢，我們可以調整。學習不該是另一種強迫自己的方式。' },
];

export default function About() {
  const hero = useVisible(0.05);
  const story = useVisible();
  const meaning = useVisible();
  const valuesSection = useVisible();
  const closing = useVisible();

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden bg-[#f2ede5]">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20close-up%20of%20guzheng%20strings%20in%20warm%20diffused%20light%2C%20bokeh%20background%2C%20minimal%20texture%2C%20muted%20gold%20and%20beige%20tones%2C%20serene%20and%20meditative&width=1920&height=800&seq=about_hero&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div ref={hero.ref} className={`relative z-10 w-full px-6 md:px-12 lg:px-20 transition-all duration-700 ${hero.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="divider-gold"></div>
            <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">About</span>
          </div>
          <h1 className="font-serif-tc text-[#2c2420] leading-[1.3] mb-6" style={{ fontSize: 'clamp(2rem, 5vw, 2.75rem)' }}>
            關於鳴箏
          </h1>
          <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] max-w-xl">
            這不只是一頁自我介紹。我想讓你了解，為什麼我選擇用這樣的方式教古箏，以及這個品牌背後真正在意的是什麼。
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={story.ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center transition-all duration-700 ${story.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="rounded-2xl overflow-hidden" style={{ aspectRatio: '4/5' }}>
              <img
                src="https://storage.helloreaddy.io/project_files/79c1f631-1cb7-456b-a6cf-fdd8ff21e9a0/4c0b23cd-9319-4a34-9477-1f1853d0f9af_compressed_023b43b8-3cc8-4b8f-84fb-ef57c0ab82cd.webp"
                alt="鳴箏老師"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-7">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">My Story</span>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-7" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                為什麼開始<br />教古箏
              </h2>
              <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] mb-5">
                我學古箏的時間很長，但真正讓我想教古箏的原因，不是因為我彈得多好，而是因為我曾經在學習裡感到窒息——被進度追著跑、被比較、被催促。
              </p>
              <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] mb-5">
                後來，我花了很長時間重新建立自己與古箏的關係。讓它從「必須練習」變回「想要靠近」。那個轉變，讓我理解：音樂本來就應該是讓人感到自在的事。
              </p>
              <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42]">
                於是，我想為那些還在找尋安心學習環境的人，提供一個不同的選擇。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meaning of Music */}
      <section className="py-24 md:py-32 bg-[#f2ede5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={meaning.ref} className={`max-w-3xl mx-auto text-center transition-all duration-700 ${meaning.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center justify-center gap-3 mb-7">
              <div className="divider-gold"></div>
              <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Philosophy</span>
              <div className="divider-gold"></div>
            </div>
            <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-8" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
              音樂對我而言的意義
            </h2>
            <blockquote className="font-serif-tc text-[#4a3e32] leading-[1.75] mb-8 italic" style={{ fontSize: '1.375rem' }}>
              「音樂不是用來比較或證明自己的。<br />它是一種回到自己的方式。」
            </blockquote>
            <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] mb-5">
              我相信，每個人都有與音樂相處的方式，只是有時候被太多的「標準」和「成果」遮住了。
            </p>
            <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42]">
              古箏的聲音有一種天然的安定感。每次撥弦，都是一次讓自己慢下來的機會。我希望你在這裡學到的，不只是彈琴的技巧，而是如何讓音樂真正地陪伴你的生活。
            </p>
          </div>
        </div>
      </section>

      <TeacherJourneySection />

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#faf8f5]">
        <div className="w-full px-6 md:px-12 lg:px-20">
          <div ref={valuesSection.ref} className={`transition-all duration-700 ${valuesSection.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-14">
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="divider-gold"></div>
                <span className="text-[0.8125rem] tracking-[0.2em] text-[#9a8470] font-medium uppercase">Values</span>
                <div className="divider-gold"></div>
              </div>
              <h2 className="font-serif-tc text-[#2c2420] leading-[1.4]" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
                我刻意不做的事
              </h2>
              <p className="mt-4 text-[1.1875rem] leading-[1.75] text-[#7a6e62] max-w-xl mx-auto">
                有些選擇，是我在建立這個品牌時，明確決定不走的路。
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {values.map((v, i) => (
                <div key={i} className="bg-[#f2ede5] rounded-2xl p-8" style={{ transitionDelay: `${i * 0.12}s` }}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#faf8f5] mb-6">
                    <i className={`${v.icon} text-[#7a6248] text-xl`}></i>
                  </div>
                  <h3 className="font-serif-tc text-[#2c2420] mb-3" style={{ fontSize: '1.375rem' }}>{v.title}</h3>
                  <p className="text-[1.1875rem] leading-[1.75] text-[#7a6e62]">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-24 md:py-28 bg-[#f2ede5]">
        <div ref={closing.ref} className={`w-full px-6 md:px-12 lg:px-20 text-center transition-all duration-700 ${closing.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="font-serif-tc text-[#2c2420] leading-[1.4] mb-6" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}>
            如果你感受到一些共鳴
          </h2>
          <p className="text-[1.1875rem] leading-[1.75] text-[#5a4e42] max-w-lg mx-auto mb-10">
            不需要立刻做決定。先了解一下教學方式，或者直接加入 LINE@ 聊聊你的狀態，都可以。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/teaching" className="px-8 py-3.5 rounded-full bg-[#4a3e32] text-[#f5f0e8] text-[1rem] font-medium hover:bg-[#3a2e22] transition-all duration-300 cursor-pointer whitespace-nowrap">
              了解教學方式
            </Link>
            <a href="https://line.me" target="_blank" rel="noopener nofollow" className="px-8 py-3.5 rounded-full border border-[#7a6248] text-[#7a6248] text-[1rem] font-medium hover:bg-[#7a6248] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap">
              加入 LINE@ 諮詢
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
